import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
// eslint-disable-next-line import/no-cycle
import { CLOUD_FUNCTIONS } from '../../auth/FirebaseContext';
import { InstantiateAttachmentMetaFromJSON } from '../../models/mrr/attachmentMeta';
import { CaseGeneral, InstantiateCaseFromJSON } from '../../models/mrr/caseGeneral';
import { InstantiateListingFromJSON, ListingGeneral } from '../../models/mrr/listingGeneral';
import ListingOrderItemGeneral, { InstantiateLOIFromJSON } from '../../models/mrr/loi';
import { InstantiateReservationFromJSON, ReservationGeneral } from '../../models/mrr/reservationGeneral';
import { InstantiateTaskFromJSON, TaskGeneral } from '../../models/mrr/taskGeneral';
import { instantiateStatementListFromJSON } from '../../pages/dashboard/statement/__legacy/statementListGeneral';
import { scopedRequestDateFormat } from '../../utils/formatTime';
import {
	endpoint_getAttachments,
	endpoint_getCases,
	endpoint_getLOIs,
	endpoint_getListing,
	endpoint_getListings,
	endpoint_getReport,
	endpoint_getReservation,
	endpoint_getReservations,
	endpoint_getStatements,
	endpoint_getTasks,
	endpoint_patchLMI,
	endpoint_postCaseComment,
	endpoint_postReservation,
	endpoint_putReservationCancel,
	endpoint_putReservationUpdate
} from '../../utils/mrr/cloudEndpointNames';
import { cloud_makeSalesforceRequest } from '../../utils/mrr/cloudFunctions';
import filterCanceledReservations from '../../utils/mrr/filterCanceledReservation';
import { OwnerReportType } from '../../utils/mrr/ownerReportConstants';
import { brandConfig } from '../../config';

const unusedBaseQuery: BaseQueryFn = (arg, api, extraOptions) => {
	return { data: null }
}

/*
RTK Query caching:

One method is based on 'subscriber count'.
A subscriber is a unique combo of call and params, so doing useGetRTKQListingsQuery('A')
and useGetRTKQListingsQuery('B') makes a subscriber count of two.
When subscriber count hits zero, basically meaning you've navigated away from the page,
the cache timer begins.
So, load page, nav away, wait the timeout, nav back, and you'll get a refetch. If you
nav back before the timout, you'll get cached data.
This can be set per-endpoint.

Another method is refetchOnMountOrArgChange, which can be set to bool (new call every mount),
or a number (new call on mount if X seconds have ellapsed). But that one doesn't (yet)
have a per-endpoint option. We have to pick a global value to share.

We're starting with a one-hour time, for both methods.

We use tags to invalidate data, forcing a refetch. This is done on every mutation. For
example, when you modify a booking (via PUT), that booking is invalidated (meaning removed
from the cache). Then, any components that subscribe to bookings AND provide the booking
tag, will refetch their data automatically.
*/

function validateStandardResultOrThrow(result: any) {
	if (typeof result.data !== 'object'
		|| result.data === null
		|| (result.data as any).status !== 200) {
		throw new Error('Invalid result.');
	}
}

//NOTE: This is used for both 'keep unused', after ref count hits zero, and 'force fetch new'
const cacheHoldTime = 60 * 60;

enum EndpointTags {
	Cases = 'Cases',
	Listings = 'Listings',
	Reservations = 'Reservations',
};

const tagArray = Object.values(EndpointTags);

// we have only ONE slices API
export const apiSlice = createApi({
	reducerPath: 'api', // this is the LOCAL path, in memory
	baseQuery: unusedBaseQuery,
	keepUnusedDataFor: cacheHoldTime, // RTKQ's default is one minute
	//TODO: refetchOnMountOrArgChange works globally, but we can't set it per endpoint.
	// SEE: https://github.com/reduxjs/redux-toolkit/issues/2333
	refetchOnMountOrArgChange: cacheHoldTime, // this should mean our cached data is never older than X seconds
	tagTypes: tagArray,
	endpoints(build) {
		return {
			// ------------------------------------------------------
			// ---------   QUERIES (GET calls, basically)   ---------
			// ------------------------------------------------------
			getRTKQCases: build.query({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getCases);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/case/list',
                        params: {listing_id: args.listingId || ''},
                        body: ''
                    };

                    const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (!Array.isArray((result.data as any).cases)) {
								throw new Error('Failed to get cases.'); //! This error is public to the user.
							}

							const { cases } = result.data as any;

							const caseModels: CaseGeneral[] = cases.map((caseJSON: any) => {
								const modelInstance = InstantiateCaseFromJSON(caseJSON);
								return modelInstance;
							});

							return { data: caseModels };
						})
						.catch((error) => {
							return { error: error }
						});

					return cloudResult;
				},
				// These can be invalidated to force refresh on update.
				providesTags: (result, error, id) => [{ type: EndpointTags.Cases, id }]
			}),
			getRTKQListingOrderItems: build.query({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getLOIs);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/listing/order_items',
                        params: {id: args.listingId || ''},
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (!Array.isArray((result.data as any).listing_order_items)) {
								throw new Error('Failed to get listing order items.'); //! This error is public to the user.
							}

							const { listing_order_items } = result.data as any;
							const loiModels: ListingOrderItemGeneral[] = listing_order_items.map((loiJSON: any) => {
								const loiModel = InstantiateLOIFromJSON(loiJSON)
								return loiModel
							})
							// this format is required by RTKQ
							return { data: loiModels };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				}
			}),
            getRTKQListings: build.query({
                queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getListings);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/listing/list',
                        params: {},
                        body: ''
                    };

                    const cloudResult = await cloudFunction(data)
                        .then(async (result) => {
							validateStandardResultOrThrow(result);

							if (!Array.isArray((result.data as any).listings)) {
								throw new Error('Failed to get properties.');
							}

							const { listings } = result.data as any;

							const listingModels: ListingGeneral[] = listings.map((listingJSON: any) => {
								const modelInstance = InstantiateListingFromJSON(listingJSON);
								return modelInstance;
							});

							return { data: listingModels };
                        })
                        .catch((error) => {
                            return { error }
                        });

					return cloudResult
				},
				// These can be invalidated to force refresh on update.
				providesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
            }),
			getRTKQObjectAttachments: build.query({
				queryFn: async (args) => {
					if (!args.objectId) {
						throw new Error('Invalid attempt to get attachments.');
					}

                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getAttachments);

                    const data = {
                        method: 'GET',
                        path: '/api/attachment/' + args.objectId,
                        params: {},
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);
							if (!Array.isArray((result.data as any).attachments)) {
								throw new Error('Failed to get attachments.'); //! This error is public to the user.
							}
							const models = (result.data as any).attachments.map((json: any) => InstantiateAttachmentMetaFromJSON(json))
							return { data: models };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error }
						});

					return cloudResult;
				}
			}),
			getRTKQOwnerReport: build.query({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getReport);

                    let endpointUrl = '/api/owner/report/';

                    switch (args.type) {
                        case OwnerReportType.Booking: {		endpointUrl += 'booking'; } break;
                        case OwnerReportType.Occupancy: {	endpointUrl += 'occupancy'; } break;
                        case OwnerReportType.Revenue: {		endpointUrl += 'revenue'; } break;

                        default: {
							throw new Error('Unknown report type.');
                        }
                    }

					// add the optional params
                    const params: Record<string, string> = {};
                    if (args.listingId) {
                        params.listing_id = args.listingId;
                    }
                    if (args.startDate) {
                        params.start_date = format(args.startDate, scopedRequestDateFormat);
                    }
                    if (args.endDate) {
                        params.end_date = format(args.endDate, scopedRequestDateFormat);
                    }

					if (args.type === OwnerReportType.Revenue && !brandConfig.legacyReports) {
						params.use_atx = '1';
					}

                    const data = {
                        method: 'GET',
                        path: endpointUrl,
                        params: params,
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);

							if (!(result.data as any).report) {
								throw new Error('Failed to get report.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							return { data: (result.data as any).report };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error }
						});

					return cloudResult;
				}
				//NOTE: transformResponse and transformErrorResponse are NOT available w/ queryFn, which we use.
				// SEE: https://redux-toolkit.js.org/rtk-query/api/createApi
			}),
			getRTKQReservations: build.query({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getReservations);

					// add the optional params
                    const params: Record<string, string> = {};
                    if (args.listingId) {
                        params.listing_id = args.listingId;
                    }
                    if (args.startDate) {
                        params.start_date = format(args.startDate, scopedRequestDateFormat);
                    }
                    if (args.endDate) {
                        params.end_date = format(args.endDate, scopedRequestDateFormat);
                    }
                    if (args.includePending) {
                        params.include_pending = args.includePending !== undefined ? args.includePending : false;
                    }

                    const data = {
                        method: 'GET',
                        path: '/api/owner/reservation/list',
                        params: params,
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then(async (result) => {
							validateStandardResultOrThrow(result);

							if (!Array.isArray((result.data as any).reservations)) {
								throw new Error('Failed to get reservations.'); //! This error is public to the user.
							}

							const { reservations } = result.data as any;

							const reservationModels: ReservationGeneral[] = reservations.map((reservationJSON: any) => {
								const modelInstance = InstantiateReservationFromJSON(reservationJSON);
								return modelInstance;
							});

							const filteredReservationModels = filterCanceledReservations(reservationModels);

							// DEV ONLY: Stress test tool (clone the data)
							// const count = filteredReservationModels.length;
							// for (let i = 0; i < 10; ++i) {
							// 	for (let r = 0; r < count; ++r) {
							// 		// eslint-disable-next-line prefer-object-spread
							// 		filteredReservationModels.push(Object.assign({}, reservationModels[r]));
							// 	}
							// }

							// this format is required by RTKQ
							return { data: filteredReservationModels };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error }
						});

					return cloudResult;
				},
				// These can be invalidated to force refresh on update.
				providesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
			}),
			getRTKQSingleListing: build.query({
				queryFn: async (args) => {
					if (!args.listingId) {
						throw new Error('Invalid attempt to get property.');
					}

					const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getListing);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/listing',
                        params: {id: args.listingId},
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);

							if (!(result.data as any).listing) {
								throw new Error('Failed to get property.'); //! This error is public to the user.
							}

							const listingModel: ListingGeneral = InstantiateListingFromJSON((result.data as any).listing);

							//NOTE: We don't filter here, (as we do with get all) because unwanted single reservations should
							//		never be in scope in the first place, and returning null/empty here is very problematic.

							// this format is required by RTKQ
							return { data: listingModel };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult
				},
				// These can be invalidated to force refresh on update.
				providesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
			}),
			getRTKQSingleReservation: build.query({
				queryFn: async (args) => {
					if (!args.reservationId) {
						throw new Error('Invalid attempt to get booking.');
					}

					const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getReservation);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/reservation',
                        params: {id: args.reservationId},
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);

							if (!(result.data as any).reservation) {
								throw new Error('Failed to get booking.'); //! This error is public to the user.
							}

							const reservationModel: ReservationGeneral = InstantiateReservationFromJSON((result.data as any).reservation);

							// this format is required by RTKQ
							return { data: reservationModel };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				},
				// These can be invalidated to force refresh on update.
				providesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
			}),
			getRTKQStatements: build.query({
				queryFn: async (args) => {
					// add the optional params
                    const params: Record<string, string> = {};
                    if (args.startDate) {
                        params.start_date = format(args.startDate, scopedRequestDateFormat);
                    }
                    if (args.endDate) {
                        params.end_date = format(args.endDate, scopedRequestDateFormat);
                    }

					const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getStatements);

                    const data = {
                        method: 'GET',
                        path: '/api/owner/report/statements',
                        params: params,
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);

							if (!(result.data as any).report) {
								throw new Error('Failed to get report.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							const json = (result.data as any).report
							return { data: instantiateStatementListFromJSON(json) };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error }
						});

					return cloudResult;
				}
			}),
			getRTKQStatementList: build.query({
				queryFn: async (args) => {
					// add the optional params
                    const params: {start_date?:string,end_date?:string} = {};
                    if (args.startDate) {
                        params.start_date = format(args.startDate, scopedRequestDateFormat);
                    }
                    if (args.endDate) {
                        params.end_date = format(args.endDate, scopedRequestDateFormat);
                    }
                    const data = {
						method: 'GET',
                        path: '/api/owner/statement',
                        params: params,
                        body: ''
                    };
					const cloudFunction = httpsCallable<typeof data,{status?:Number,statements:Statement[]}>(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getStatements);
					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result);

							if (!result.data.statements || result.data.status !== 200) {
								throw new Error('Failed to get report.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							return {data: result.data.statements};
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error }
						});
					return cloudResult;
				}
			}),
			getRTKQTasks: build.query({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getTasks);

					// add the optional params
                    const params: Record<string, string> = {};

                    if (args.listingId) {
                        params.listing_id = args.listingId;
                    }
                    if (args.startDate) {
                        params.start_date = format(args.startDate, scopedRequestDateFormat);
                    }
                    if (args.endDate) {
                        params.end_date = format(args.endDate, scopedRequestDateFormat);
                    }

                    const data = {
                        method: 'GET',
                        path: '/api/owner/task/list',
                        params: params,
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (!Array.isArray((result.data as any).tasks)) {
								throw new Error('Failed to get tasks.'); //! This error is public to the user.
							}

							const { tasks } = result.data as any;
							const taskModels: TaskGeneral[] = tasks.map((taskJSON: any) => {
								const taskModel = InstantiateTaskFromJSON(taskJSON)
								return taskModel
							})
							// this format is required by RTKQ
							return { data: taskModels };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				}
			}),
			// ------------------------------------------------------
			// ---------  MUTATIONS (patch/post/put calls)  ---------
			// ------------------------------------------------------
			patchRTKQListingCOI: build.mutation({
				queryFn: async (args) => {
					if (!args.item_id || !args.status) {
						throw new Error('COI update incomplete.');
					}

                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_patchLMI);

                    const data = {
                        method: 'PATCH',
                        path: '/api/owner/listing/coi_status',
                        params: args, // this patch endpoint takes the args as params, and no body
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (typeof (result.data as any).management_item !== 'object') {
								throw new Error('Failed to get updated listing item.'); //! This error is public to the user.
							}

							//NOTE: We have a full LMI model here, but we don't need it (yet). RTKQ will reload the listing via invalidate,
							//		which rebuilds with a fresh LMI child model. Still, if needed, use this:
							// const newLMIModel = InstantiateCOIFromJSON(result.data.management_item); << needs to be exported

							// this format is required by RTKQ
							return { data: true };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				},
				// This endpoint causes items with these tags to be refetched.
				invalidatesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
			}),
			postRTKQCaseComment: build.mutation({
				queryFn: async (args) => {
                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_postCaseComment);

                    const data = {
                        method: 'POST',
                        path: '/api/owner/case/comment',
                        params: {id: args.id},
                        body: {body: args.body} // this 'body' is the actual comment text entered by the user
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (!(result.data as any).case_comment) { //NOTE: This is redundant. We don't (yet) use the response object.
								throw new Error('Failed to add comment.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							return { data: true };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				},
				// This endpoint causes items with these tags to be refetched.
				invalidatesTags: (result, error, id) => [{ type: EndpointTags.Cases, id }]
			}),
			postRTKQReservation: build.mutation({
				queryFn: async (args) => {
					if (!args.check_in || !args.check_out || !args.listing || !args.listing.id) {
						throw new Error('New booking form incomplete.');
					}

					//NOTE: We have to carefully check our types here! It's not bool, due to the RHF 'checkboxes' (radios).
					//		They give us string "true"/"false" (and it's not trivial to re-cast that on the way in).
					const boolGuestIsOwnerArg = args.guest_is_owner === 'true' || args.guest_is_owner === true;
					const boolOwnerPayFinalClean = args.guest_pay_final_cleaning === 'false' || args.guest_pay_final_cleaning === false;
					const boolNoDepartureClean = args.no_departure_clean === 'true' || args.no_departure_clean === true;

					const body: any = {
						check_in: args.check_in,
						check_out: args.check_out,
						listing_id: args.listing.id,
						no_departure_clean: boolNoDepartureClean,
						type: boolGuestIsOwnerArg ? 'Owner' : 'Guest of Owner',
						special_requests_detail: args.special_requests_detail
					};

					if (boolGuestIsOwnerArg) {
						body.guest_id = args.externalAccountID;
						body.owner_pays_guest_clean = true;
					}
					else {
						if (!args.guest_email || !args.guest_first_name || !args.guest_last_name) {
							throw new Error('New booking guest info incomplete.');
						}

						body.guest_email = args.guest_email;
						body.guest_first_name = args.guest_first_name;
						body.guest_last_name = args.guest_last_name;
						if (args.guest_phone) {
							body.guest_phone = args.guest_phone;
						}
						body.owner_pays_guest_clean = boolOwnerPayFinalClean;
					}

					body.stage = 'Confirmed';

                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_postReservation);

                    const data = {
                        method: 'POST',
                        path: '/api/owner/reservation/booking',
                        params: {},
                        body: body
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)
							console.log(result)
							if (!(result.data as any).reservation) {
								throw new Error('Failed to get updated booking.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							return { data: result.data as Partial<ReservationGeneral> };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				},
				// This endpoint causes items with these tags to be refetched.
				invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
			}),
			putRTKQReservationCancel: build.mutation({
				queryFn: async (args) => {
					if (!args.reservationID) {
						throw new Error('Cancelation info incomplete.');
					}

                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_putReservationCancel);

                    const data = {
                        method: 'PUT',
                        path: '/api/owner/reservation/cancel',
                        params: {id: args.reservationID},
                        body: ''
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)

							if (!(result.data as any).reservation) {
								throw new Error('Unable to cancel booking.'); //! This error is public to the user.
							}

							// this format is required by RTKQ
							return { data: true };
						})
						.catch((error) => {
							// this format is required by RTKQ
							return { error: error };
						});

					return cloudResult;
				},
				// This endpoint causes items with these tags to be refetched.
				invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
			}),
			putRTKQReservationUpdate: build.mutation({
				queryFn: async (args) => {
					if (!args.id || !args.check_in || !args.check_out) {
						throw new Error('Booking form incomplete.');
					}

                    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_putReservationUpdate);

					const body = {
						//NOTE: Before the move to single-endpoint, this call was sending up id in the body, and GCF was not removing it.
						//		It's not actually needed by the backend (which takes id as a param), so it's been removed.
						check_in: args.check_in.toISOString(),
						check_out: args.check_out.toISOString()
					};

                    const data = {
                        method: 'PUT',
                        path: '/api/owner/reservation/update',
                        params: {id: args.id},
                        body: body
                    };

					const cloudResult = await cloudFunction(data)
						.then((result) => {
							validateStandardResultOrThrow(result)
							const { reservation, status } = result.data as any
							if (status !== 200) {
								throw new Error('Failed to get updated booking.'); //! This error is public to the user.
							}
							// this format is required by RTKQ
							return { data: reservation };
						})
						.catch((error) => {
							// this format is required by RTKQ
							console.log('put reservation update', error)
							return { error: error };
						});

					return cloudResult;
				},
				// This endpoint causes items with these tags to be refetched.
				invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
			})
		}
	},
});


// Export the auto-generated hooks our RTK query endpoint
export const {
	useGetRTKQCasesQuery,
	useGetRTKQListingOrderItemsQuery,
	useGetRTKQListingsQuery,
	useGetRTKQObjectAttachmentsQuery,
	useGetRTKQOwnerReportQuery,
	useGetRTKQReservationsQuery,
	useGetRTKQSingleListingQuery,
	useGetRTKQSingleReservationQuery,
	useGetRTKQStatementsQuery,
	useGetRTKQStatementListQuery,
	useGetRTKQTasksQuery,
	usePatchRTKQListingCOIMutation,
	usePostRTKQCaseCommentMutation,
	usePostRTKQReservationMutation,
	usePutRTKQReservationCancelMutation,
	usePutRTKQReservationUpdateMutation
} = apiSlice;
